import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import {
  GetRecommendedUpNextQuery,
  GetRecommendedUpNextQueryVariables,
  RecommendUpNextContent,
} from '@/types/codegen-federation'
import { isDefined } from '@/utils/types'
import { getWebClient } from '../ApolloClient'
import { GET_RECOMMENDED_UP_NEXT } from './queries'
import { RecommendedUpNextResult } from './types'

export const useGetRecommendedUpNext = (projectSlug: string): RecommendedUpNextResult => {
  const client = getWebClient()
  const { data, loading } = useQuery<GetRecommendedUpNextQuery, GetRecommendedUpNextQueryVariables>(
    GET_RECOMMENDED_UP_NEXT,
    {
      client,
      variables: { projectSlug },
      errorPolicy: 'all',
    },
  )

  return useMemo(() => {
    const recommendedUpNext = data?.recommendedUpNextV2?.content?.filter(isDefined) ?? []

    const shouldAddHomesteadSeries =
      projectSlug === 'homestead' &&
      !recommendedUpNext.some(
        (item) => item?.title?.project?.slug === homesteadSeriesRecommendation?.title?.project?.slug,
      )
    if (shouldAddHomesteadSeries) {
      recommendedUpNext.unshift(homesteadSeriesRecommendation)
    }

    return { recommendedUpNext, loading }
  }, [data, loading, projectSlug])
}

// TODO remove after Homestead The Movie has been streaming for a while.
const homesteadSeriesRecommendation: RecommendUpNextContent = {
  __typename: 'RecommendUpNextContent',
  id: '9cadb4a0-df6c-4c13-bc1e-f09c972e0c0b',
  title: {
    __typename: 'ContentSeries',
    id: 'homestead-series',
    image: {
      __typename: 'ContentImage',
      cloudinaryPath: 'v1734637062/studio-app/catalog/e8217995-8ab0-4266-b8d1-3e79b543d317',
      aspect: '16:9',
      category: 'TITLE_ART',
    },
    name: 'Homestead: The Series',
    earlyAccessFor: [],
    slug: 'homestead-series',
    userHasEarlyAccess: false,
    advisories: [],
    availableLanguages: [],
    project: {
      __typename: 'Project',
      id: 'homestead-project',
      slug: 'homestead-series',
      groups: [],
      interestInitiatives: [],
      totalUniqueInterestAmount: 0,
      totalUniqueInterestUsers: 0,
    },
  },
  watchable: {
    __typename: 'ContentEpisode',
    id: 'homestead-series',
    vmapUrl: '',
    episodeNumber: 1,
    isVisible: true,
    project: {
      __typename: 'Project',
      id: 'homestead-project',
      slug: 'homestead-series',
      groups: [],
      interestInitiatives: [],
      totalUniqueInterestAmount: undefined,
      totalUniqueInterestUsers: undefined,
    },
    season: {
      __typename: 'ContentSeason',
      id: 'homestead-season-1',
      seasonNumber: 1,
      project: {
        __typename: 'Project',
        id: 'homestead-project',
        slug: 'homestead-series',
        groups: [],
        interestInitiatives: [],
        totalUniqueInterestAmount: 0,
        totalUniqueInterestUsers: 0,
      },
      series: {
        __typename: 'ContentSeries',
        id: 'homestead-series',
        image: {
          __typename: 'ContentImage',
          cloudinaryPath: 'v1734637062/studio-app/catalog/e8217995-8ab0-4266-b8d1-3e79b543d317',
          aspect: '16:9',
          category: 'TITLE_ART',
        },
        name: 'Homestead: The Series',
        earlyAccessFor: [],
        slug: 'homestead-series',
        userHasEarlyAccess: false,
        advisories: [],
        availableLanguages: [],
        project: {
          __typename: 'Project',
          id: 'homestead-project',
          slug: 'homestead-series',
          groups: [],
          interestInitiatives: [],
          totalUniqueInterestAmount: 0,
          totalUniqueInterestUsers: 0,
        },
      },
    },
    slug: 'homestead-series-episode-1',
  },
}
